import React, { useEffect, useRef, useState } from 'react';
import { setUserHasInteracted } from '../utils/audioState';
import { effectiveMusicVolume, subscribeSettings } from '../utils/settings';

const MusicPlayer = ({ musicFile, paused = false }) => {
  const [volume, setVolume] = useState(() => effectiveMusicVolume());

  // Live-update volume when user changes settings
  useEffect(() => subscribeSettings(() => setVolume(effectiveMusicVolume())), []);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const loadedMusicFileRef = useRef(null);
  const [autoPlayBlocked, setAutoPlayBlocked] = useState(false);

  useEffect(() => {
    // When musicFile is temporarily null/undefined (e.g. during area transitions),
    // keep the current track playing — don't pause or clean up.
    // The new area's game_state will arrive shortly with the correct music.
    if (!musicFile) {
      return; // No cleanup — keep current audio playing through transitions
    }

    const audio = audioRef.current;
    if (!audio) return;

    // Convert MIDI files to MP3 equivalents
    let actualMusicFile = musicFile;
    if (musicFile.toLowerCase().endsWith('.mid')) {
      actualMusicFile = musicFile.replace(/\.mid$/i, '.mp3');
    }

    // Check if this is the same music as currently loaded
    if (actualMusicFile === loadedMusicFileRef.current) {
      setCurrentTrack(musicFile);
      // Resume playback if it was paused (e.g. by a transient null musicFile)
      if (audio.paused && !paused && audio.src) {
        audio.play().then(() => {
          setIsPlaying(true);
        }).catch(() => {});
      }
      return; // No cleanup needed — same track continues
    }

    // Different music file - need to change tracks
    console.log('Changing music track from', loadedMusicFileRef.current, 'to', actualMusicFile);

    // Stop current music if playing
    if (loadedMusicFileRef.current && !audio.paused) {
      audio.pause();
      audio.currentTime = 0;
    }

    // Set new track
    setCurrentTrack(musicFile);
    loadedMusicFileRef.current = actualMusicFile;

    // Load music file
    const musicPath = `/music/${actualMusicFile}`;
    audio.src = musicPath;
    audio.volume = volume;
    audio.loop = true;

    // Auto-play unless externally paused (e.g. during combat)
    if (!paused) {
      audio.play().then(() => {
        setIsPlaying(true);
        setAutoPlayBlocked(false);
        setUserHasInteracted(true);
      }).catch((error) => {
        console.log('Auto-play blocked, waiting for user click:', error.message);
        setIsPlaying(false);
        setAutoPlayBlocked(true);
      });
    }

    // Only pause audio when switching to a genuinely different track
    return () => {
      if (audio && !audio.paused) {
        audio.pause();
      }
    };
  }, [musicFile, paused]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply live volume changes from settings to the running audio element
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.volume = volume;
  }, [volume]);

  // Pause/resume when external paused prop changes (e.g., during combat)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (paused && !audio.paused) {
      audio.pause();
    } else if (!paused && audio.paused && audio.src && loadedMusicFileRef.current) {
      audio.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {});
    }
  }, [paused]);

  // Global click listener: if auto-play was blocked, the very first click
  // anywhere on the page will start the music automatically
  useEffect(() => {
    if (!autoPlayBlocked) return;

    const handleFirstInteraction = () => {
      const audio = audioRef.current;
      if (audio && audio.src && audio.paused && !paused) {
        audio.play().then(() => {
          setIsPlaying(true);
          setAutoPlayBlocked(false);
          setUserHasInteracted(true);
          console.log('Music auto-started after first user interaction on page');
        }).catch(err => {
          console.warn('Still could not play after interaction:', err);
        });
      }
    };

    document.addEventListener('click', handleFirstInteraction, { once: true });
    document.addEventListener('keydown', handleFirstInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };
  }, [autoPlayBlocked]);

  const toggleMusic = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => {
        setIsPlaying(true);
        setAutoPlayBlocked(false);
        setUserHasInteracted(true);
        console.log('Music started after user interaction - auto-play now enabled');
      }).catch(error => {
        console.warn('Could not resume music:', error);
      });
    }
  };

  if (!musicFile) {
    return null;
  }

  return (
    <div className="music-player">
      <audio 
        ref={audioRef}
        loop={true}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          console.log('Track ended, restarting due to loop');
          const audio = audioRef.current;
          if (audio) {
            audio.currentTime = 0;
            audio.play().catch(error => {
              console.warn('Could not restart track:', error);
              setIsPlaying(false);
            });
          }
        }}
        onError={(e) => {
          console.error('Audio error:', e);
          setIsPlaying(false);
        }}
      />
      
      <button
        onClick={toggleMusic}
        className={`music-btn${autoPlayBlocked && !isPlaying ? ' autoplay-blocked' : ''}`}
        title={isPlaying ? 'Pause music' : autoPlayBlocked ? 'Click to start music' : 'Play music'}
      >
        {isPlaying ? '▶️' : '⏸️'}
      </button>
      
      <span className="music-track-name">🎵 {musicFile.replace(/\.(mid|mp3)$/i, '')}</span>
      
      {isPlaying ? (
        <span className="music-status">(Playing)</span>
      ) : autoPlayBlocked ? (
        <span className="music-status" style={{ color: 'var(--npc-amber)', fontWeight: 'bold', opacity: 1 }}>
          (Click ▶️ to start!)
        </span>
      ) : currentTrack && (
        <span className="music-status">(Click to play)</span>
      )}
    </div>
  );
};

export default MusicPlayer;
